import React, { useState, useEffect, useRef } from 'react'
import { useRouter } from '~/hooks/useRouter'
import classes from './Menu.module.css'
import Image from '~/components/common/Image'
import Button from '@mui/material/Button'
import { CartIcon } from '~/components/Icons/Icons'
import MenuMui from '@mui/material/Menu'
import MenuIcon from '@mui/icons-material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { grey } from '@mui/material/colors'
import { FormattedMessage } from 'react-intl'
import Select from '~/components/Select/Select'
import { useCart } from '~/context/cartContext'
import { Badge } from '@mui/material'
import { CustomButton } from '~/components/CustomButton/CustomButton'
import {
  LocalizedLink,
  useLocalizedRouter,
} from '~/components/LocalizedLink/LocalizedLink'

export const Menu: React.FC = () => {
  const router = useRouter()
  const { asPath } = router
  const { push, locale } = useLocalizedRouter()

  const isLanding =
    asPath === '/' ||
    asPath === '/ro' ||
    asPath === '/ru' ||
    asPath.startsWith('/ro?') ||
    asPath.startsWith('/ru?') ||
    asPath.startsWith('/?')

  const { itemCount } = useCart()

  // Menu open state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  // Header visibility on scroll (mobile)
  const lastScrollY = useRef(0)
  const [showHeader, setShowHeader] = useState(true)

  // Background on scroll (all sizes)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    lastScrollY.current = window.pageYOffset

    const handleScroll = () => {
      const currentScrollY = window.pageYOffset

      // background toggle for ALL screen sizes
      setIsScrolled(currentScrollY > 6)

      // hide/show header ONLY on mobile
      if (window.innerWidth <= 900) {
        if (currentScrollY <= 0) {
          setShowHeader(true)
        } else if (currentScrollY > lastScrollY.current) {
          setShowHeader(false)
        } else {
          setShowHeader(true)
        }
      }

      lastScrollY.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // important if user refreshes while scrolled

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const headerClass = [
    classes.header,
    !showHeader ? classes.hidden : '',
    // on landing: beige only after scroll; on other pages: always beige
    isLanding ? (isScrolled ? classes.scrolled : '') : classes.scrolled,
  ]
    .filter(Boolean)
    .join(' ')

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const handleLocaleChange = (newLocale: string) => {
    if (typeof window === 'undefined') return
    if (newLocale === locale) return
    document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=${60 * 60 * 24 * 30}`
    const currentPath = window.location.pathname + window.location.search
    const pathWithoutLocale =
      currentPath.replace(/^\/(ro|ru)(?=\/|$)/, '') || '/'
    const target =
      newLocale === 'ro'
        ? pathWithoutLocale
        : `/${newLocale}${pathWithoutLocale === '/' ? '' : pathWithoutLocale}`
    window.location.assign(target)
  }

  return (
    <header className={headerClass}>
      <div className={classes.headerContainer}>
        <div className={classes.logo}>
          <LocalizedLink href="/" aria-label="Home">
            <Image
              width={145}
              height={23}
              src="/logo-white.svg"
              alt="Dulap Studio Logo"
              priority
            />
          </LocalizedLink>
        </div>

        <Button
          id="basic-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          className={classes.menuButton}
          onClick={handleClick}
        >
          <MenuIcon sx={{ color: grey[100] }} fontSize="large" />
        </Button>

        <MenuMui
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          sx={{
            width: '200px',
            fontSize: '12px',
            fontFamily: 'Onest, sans-serif',
            '& .MuiPaper-root': {
              backgroundColor: 'rgba(201, 185, 171, 0.85)',
              color: '#fffdf7',
              border: '1px solid #e0e0e0',
              boxShadow: 'none',
              borderRadius: '24px',
              cornerShape: 'superellipse(1.18)',
              width: '150px',
            },
          }}
        >
          <MenuItem
            onClick={(e) => e.stopPropagation()}
            disableRipple
            disableTouchRipple
          >
            <Select
              options={['ro', 'ru']}
              defaultValue={locale}
              onChange={(v) => {
                handleClose()
                handleLocaleChange(v)
              }}
              size="small"
              transparent
            />
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleClose()
              push('/catalog')
            }}
            className={classes.menuItem}
          >
            <FormattedMessage id="homepage.menu.productsTitle" />
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleClose()
              push('/about-us')
            }}
            className={classes.menuItem}
          >
            <FormattedMessage id="homepage.menu.aboutUsTitle" />
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleClose()
              push('/blog')
            }}
            className={classes.menuItem}
          >
            <FormattedMessage id="homepage.menu.blogTitle" />
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleClose()
              push('/contacts')
            }}
            className={classes.menuItem}
          >
            <FormattedMessage id="homepage.menu.contactsTitle" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose()
              push('/contact/book-visit')
            }}
            className={classes.menuItem}
          >
            <CustomButton size="medium" variant="danger" outlined>
              <FormattedMessage id="homepage.menu.btnCTA" />
            </CustomButton>
          </MenuItem>

          <MenuItem onClick={handleClose}>
            <LocalizedLink href="/cart" aria-label="Cart">
              <Badge badgeContent={itemCount} color="primary">
                <CartIcon />
              </Badge>
            </LocalizedLink>
          </MenuItem>
        </MenuMui>

        <nav className={classes.navigation}>
          <LocalizedLink href="/contact/book-visit">
            <CustomButton size="medium" variant="danger" outlined>
              <FormattedMessage id="homepage.menu.btnCTA" />
            </CustomButton>
          </LocalizedLink>
          <LocalizedLink href="/catalog">
            <FormattedMessage id="homepage.menu.productsTitle" />
          </LocalizedLink>
          <LocalizedLink href="/about-us">
            <FormattedMessage id="homepage.menu.aboutUsTitle" />
          </LocalizedLink>
          <LocalizedLink href="/blog">
            <FormattedMessage id="homepage.menu.blogTitle" />
          </LocalizedLink>
          <LocalizedLink href="/contacts">
            <FormattedMessage id="homepage.menu.contactsTitle" />
          </LocalizedLink>

          <Select
            options={['ro', 'ru']}
            defaultValue={locale}
            onChange={handleLocaleChange}
            size="small"
            transparent
          />

          <div className={classes.icons}>
            <LocalizedLink href="/cart" aria-label="Cart">
              <Badge badgeContent={itemCount} color="primary">
                <CartIcon />
              </Badge>
            </LocalizedLink>
          </div>
        </nav>
      </div>
    </header>
  )
}
